import { memo, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate, useSearch } from "@tanstack/react-router";

import AdminBreadcrumb from "../components/AdminBreadcrumb";
import ClientSummary from "../components/ClientAdd/ClientSummary";
import { ClientAddForm } from "../components/ClientAdd/ClientAddForm";
import { MonitoringTabs } from "../components/ClientAdd/MonitoringTab";

import { useClientForm } from "../hooks/useClientForm";
import { useClientPlans } from "../hooks/useClientPlans";
import { useAddClient, useEditClient } from "../api/client-add.queries";
import {
  CLIENT_ADD_DEFAULT_PLATFORMS,
  getPlanById,
  MONITORING_TO_SUBSCRIPTION_TYPE,
} from "@/constants/plans";
import { getMonitoringTypeFromSubscription } from "../utils/subscriptionToMonitoring";
import type { IClientEditPayload } from "../types/client.types";
import type { MonitoringType } from "@/constants/monitoring";
import type {
  ClientOverviewResponse,
  ClientOverviewWithSubscriptionsResponse,
  SubscriptionDetails,
} from "../types/client.types";

const TAB_CONFIGS: { value: MonitoringType; label: string }[] = [
  { value: "keyword", label: "Keyword Monitoring" },
  { value: "influencer", label: "Influencer Monitoring" },
  { value: "source", label: "Source Monitoring" },
  { value: "pulse", label: "Pulse Monitoring" },
];

function hasSubscriptions(
  data:
    | ClientOverviewResponse
    | ClientOverviewWithSubscriptionsResponse
    | undefined,
): data is ClientOverviewWithSubscriptionsResponse {
  return (
    !!data &&
    "subscriptions" in data &&
    Array.isArray(
      (data as ClientOverviewWithSubscriptionsResponse).subscriptions,
    )
  );
}

/** Subscription to deactivate when a monitoring tab is turned off. */
function getSubscriptionIdToDeactivate(
  initialSubscriptions: SubscriptionDetails[] | undefined,
  tabDisabled: Record<MonitoringType, boolean>,
): string | undefined {
  if (!initialSubscriptions?.length) return undefined;
  for (const tab of TAB_CONFIGS) {
    if (!tabDisabled[tab.value]) continue;
    const wantType = MONITORING_TO_SUBSCRIPTION_TYPE[tab.value];
    if (!wantType) continue;
    const sub = initialSubscriptions.find(
      (s) => getPlanById(s.plan_id)?.subscription_type === wantType,
    );
    if (sub) return sub.subscription_id;
  }
  return undefined;
}

function ClientAddContainer() {
  const editSearch = useSearch({
    from: "/admin/client/edit",
    shouldThrow: false,
  }) as { clientId?: string; clientName?: string; mode?: string } | undefined;
  const { mode } = editSearch ?? {
    clientId: undefined,
    clientName: undefined,
    mode: undefined,
  };
  const { state } = useLocation();

  const clientData = (
    state as {
      clientData?:
        | ClientOverviewResponse
        | ClientOverviewWithSubscriptionsResponse;
    }
  )?.clientData;

  const isEditMode = mode === "edit";
  const initialSubscriptions = hasSubscriptions(clientData)
    ? clientData.subscriptions
    : undefined;

  const navigate = useNavigate();
  const { form, showPassword, setShowPassword, isValid } =
    useClientForm(isEditMode);
  const {
    selectedPlans,
    expandedIds,
    tabDisabled,
    totalPrice,
    subscriptions,
    hasSelections,
    keywordCards,
    influencerCards,
    sourceCards,
    pulseCards,
    handlePlatformChange,
    handleDisabledChange,
    toggleExpanded,
  } = useClientPlans(initialSubscriptions);

  useEffect(() => {
    if (!clientData?.user) return;
    const { user } = clientData;
    form.reset({
      email: user.email ?? "",
      first_name: user.first_name ?? "",
      last_name: user.last_name ?? "",
      username: user.username ?? "",
    });
  }, [clientData, form]);

  const { mutate: addClient } = useAddClient();
  const { mutate: editClient } = useEditClient();

  const canCreateAccount = isValid && hasSelections;
  const canUpdateAccount = isValid && !!clientData?.user;

  const tabConfigs = useMemo(() => {
    const cardsByType = {
      keyword: keywordCards,
      influencer: influencerCards,
      source: sourceCards,
      pulse: pulseCards,
    };
    return TAB_CONFIGS.map((tab) => ({
      ...tab,
      cards: cardsByType[tab.value as keyof typeof cardsByType],
    }));
  }, [keywordCards, influencerCards, sourceCards, pulseCards]);

  const handleSubmit = form.handleSubmit((data) => {
    if (isEditMode && clientData?.user) {
      const existingPlanIds = new Set(
        initialSubscriptions?.map((s) => s.plan_id) ?? [],
      );
      const firstNewPlan = Array.from(selectedPlans.values()).find(
        (p) => !existingPlanIds.has(p.plan_id),
      );
      const deactivateId = getSubscriptionIdToDeactivate(
        initialSubscriptions,
        tabDisabled,
      );
      const remainingCount =
        (initialSubscriptions?.length ?? 0) - (deactivateId ? 1 : 0);
      const addingPlan =
        !!firstNewPlan?.plan_id && (firstNewPlan.platforms?.length ?? 0) > 0;
      const hasActivePlan = remainingCount > 0 || addingPlan;

      const payload: IClientEditPayload = {
        first_name: data.first_name,
        last_name: data.last_name,
        username: data.username,
        role_name: "manager",
        user_status: hasActivePlan ? "active" : "inactive",
      };

      if (addingPlan && firstNewPlan) {
        payload.add_plan = {
          plan_id: firstNewPlan.plan_id,
          selected_platforms: firstNewPlan.platforms,
          duration_months: 1,
        };
      }
      if (deactivateId) {
        payload.deactivate_plan_subscription_id = deactivateId;
      }
      for (const sub of initialSubscriptions ?? []) {
        const monitoringType = getMonitoringTypeFromSubscription(sub);
        const selectedKey = [...selectedPlans.keys()].find((k) =>
          k.startsWith(`${monitoringType}-`),
        );
        const selected = selectedKey
          ? selectedPlans.get(selectedKey)
          : undefined;
        if (
          selected &&
          sub.subscription_id &&
          JSON.stringify([...(sub.platforms ?? [])].sort()) !==
            JSON.stringify([...selected.platforms].sort())
        ) {
          payload.edit_plan = {
            subscription_id: sub.subscription_id,
            selected_platforms: selected.platforms,
          };
          break;
        }
      }

      editClient({
        userId: clientData.user.user_id,
        ...payload,
      });
      return;
    }
    addClient({
      email: data.email,
      password: data.password ?? "",
      username: data.username,
      first_name: data.first_name,
      last_name: data.last_name,
      provider: "email",
      subscriptions: Array.from(selectedPlans.values()).map((plan) => ({
        plan_id: plan.plan_id,
        selected_platforms: [...CLIENT_ADD_DEFAULT_PLATFORMS],
        duration_months: 1,
      })),
    });
  });

  return (
    <main className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
      <div className="flex flex-col gap-4">
        <div className="flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between">
          <AdminBreadcrumb
            title={isEditMode ? "Edit client" : "Add new client"}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <div className="scrollbar-thin grid min-h-0 auto-rows-max gap-4 pb-20">
          <div className="flex w-full justify-between gap-8 md:flex-col-reverse lg:flex-row">
            {/* Action Buttons */}
            <div className="flex w-auto flex-row gap-2">
              <Button
                variant="default"
                className="disabled:bg-play h-[52px] w-auto p-3"
                disabled={isEditMode ? !canUpdateAccount : !canCreateAccount}
                onClick={handleSubmit}
              >
                <i className="ri-add-line text-md font-light" />
                {isEditMode ? "Update Account" : "Create Account"}
              </Button>
              <Button
                icon
                variant="default"
                className="bg-black-950 hover:bg-black-950/80 flex h-[52px] w-auto flex-row gap-2 p-3"
                onClick={() => navigate({ to: "/admin/client" })}
              >
                Cancel
              </Button>
            </div>

            {/* Main Content */}
            <div className="w-full max-w-[1000px] space-y-[32px]">
              <div className="w-full overflow-hidden">
                <ClientAddForm
                  form={form}
                  showPassword={showPassword}
                  onTogglePassword={() => setShowPassword((v) => !v)}
                  isEdit={isEditMode}
                  clientData={clientData}
                />
              </div>

              <MonitoringTabs
                tabConfigs={tabConfigs}
                tabDisabled={tabDisabled}
                selectedPlans={selectedPlans}
                isEditMode={isEditMode}
                onDisabledChange={handleDisabledChange}
                onPlatformChange={handlePlatformChange}
              />
            </div>

            {/* Summary Sidebar */}
            <ClientSummary
              first_name={form.watch("first_name")}
              last_name={form.watch("last_name")}
              username={form.watch("username")}
              userEmail={form.watch("email")}
              password={form.watch("password") ?? ""}
              subscriptions={subscriptions}
              totalPrice={totalPrice}
              expandedIds={expandedIds}
              onToggleExpanded={toggleExpanded}
              isEdit={isEditMode}
            />
          </div>
        </div>
      </div>
    </main>
  );
}

export default memo(ClientAddContainer);
